import { useState } from 'react';
import { useNotification, useTranslation } from '@topcon/topcon-utils';

import { useUpdateRequestBusinessPartner } from '../../pages/RequestBusinessPartner/businessPartnerRequest.mutations';
import { RequestBusinessPartnerPatchBody } from '../../api/generated/pm';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';
import { getInitialFormState } from '../../utils/form';
import {
  baseRequestBusinessPartnerFormSchema,
  RequestBusinessPartnerFormData,
  requestBusinessPartnerFormSchema,
} from './useBusinessPartnerForm';

const FORM_NAME = 'CompanyDivisionForm';

interface UseBusinessPartnerFormProps {
  defaultValues?: Partial<RequestBusinessPartnerFormData>;
}

function useRequestBusinessPartnerForm({ defaultValues }: UseBusinessPartnerFormProps) {
  const { mutateAsync: updateRequest, isPending: isPendingUpdateRequest } =
    useUpdateRequestBusinessPartner();
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const { t } = useTranslation(['common']);
  const [formFields, setFormFields] = useState<RequestBusinessPartnerFormData>(
    getInitialFormState(requestBusinessPartnerFormSchema, defaultValues)
  );

  const handleUpdateRequestBusinessPartner = async (id: string): Promise<boolean> => {
    if (!validateForm(formFields)) {
      notificationService.addNotification(
        t('error', { ns: 'common' }),
        t('formValidationError', { ns: 'common' }),
        'error'
      );
      return false;
    }

    try {
      const { name, ...companyAddress } = formFields;
      const patchBody: RequestBusinessPartnerPatchBody = {
        name,
        companyAddress,
      };

      const updated = await updateRequest({ id, body: patchBody });
      // 202 (accepted for asynchronous processing) carries no body: the change
      // is not applied yet, so report it as not updated.
      return updated !== undefined;
    } catch (err) {
      if (!showError(err)) {
        const errorMessage = err instanceof Error ? err.message : 'errorDescription';
        notificationService.addNotification(
          t('error', { ns: 'common' }),
          `${t('createFailed', { ns: 'common' })}: ${t(errorMessage, { ns: 'common' })}`,
          'error'
        );
      }
      return Promise.reject(err);
    }
  };

  const validateForm = (fields: RequestBusinessPartnerFormData) => {
    const result = baseRequestBusinessPartnerFormSchema.safeParse(fields);
    return result.success;
  };

  const clearFields = () => {
    setFormFields(getInitialFormState(requestBusinessPartnerFormSchema, defaultValues));
  };

  const isPending = isPendingUpdateRequest;

  return {
    formFields,
    setFormFields,
    validateForm,
    isPending,
    handleUpdateRequestBusinessPartner,
    clearFields,
    formName: FORM_NAME,
  };
}

export { useRequestBusinessPartnerForm, requestBusinessPartnerFormSchema };
